import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Home.css';
import ResumePointsTransparent from '../assets/images/home_resume_points_transparent.png';
import Logo from '../assets/images/logo.png';

// Import icons from react-icons
import { FaUserEdit, FaFileDownload, FaCheckCircle, FaInfoCircle, FaRocket, FaUserPlus } from 'react-icons/fa';
import { BsFileEarmarkText } from 'react-icons/bs';
import { GiMagicSwirl } from 'react-icons/gi';
import { RiMagicLine } from 'react-icons/ri';

const Home = () => {
  const { isLoggedIn } = useAuth();
  useEffect(() => {
    if (isLoggedIn) {
      document.title += ' - Home';
    }
    // This will clean up the title when component unmounts
    return () => {
      document.title = 'CVance'; // Default title
    };
  }, []);
  
  return (
    <div className="home-page">
      {/* Hero section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">✨ Tailor Your Resume for Every Job Application</h1>
            <p className="hero-subtitle">
              Optimize your resume for each job opportunity to increase your chances of landing interviews.
            </p>
            <div className="hero-actions">
              {isLoggedIn ? (
                <Link to="/tailor" className="btn btn-primary btn-lg">
                  <RiMagicLine size={20} /> Tailor My Resume
                </Link>
              ) : (
                <Link to="/register" className="btn btn-primary btn-lg">
                  <FaRocket size={18} /> Get Started
                </Link>
              )}
              <a href="#how-it-works" className="btn btn-outline btn-lg">
                <FaInfoCircle size={18} /> Learn More
              </a>
            </div>
          </div>
          {/* <div className="hero-image">
            <img 
              src={Logo} 
              alt="Resume tailoring illustration" 
              className="hero-img"
            />
          </div> */}
        </div>
      </section>
      
      {/* Features section */}
      <section className="features-section" id="how-it-works">
        <div className="container">
          <div className="section-header text-center">
            <h2>How It Works</h2>
            <p className='features-section-para'>Our platform makes it easy to customize your resume in just a few steps</p>
          </div>
          
          <div className="features-list">
            <div className="feature-card">
              <div className="feature-icon">
                <FaUserEdit size={24} />
              </div>
              <h3 className="feature-title">Create Your Profile</h3>
              <p className="feature-text">
                Enter your education, experience, skills, and projects once to use across all your applications.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <BsFileEarmarkText size={24} />
              </div>
              <h3 className="feature-title">Paste Job Description</h3>
              <p className="feature-text">
                Add the job posting you're applying for so we can analyze key requirements and keywords.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <GiMagicSwirl size={28} />
              </div>
              <h3 className="feature-title">Generate Tailored Resume</h3>
              <p className="feature-text">
                Our AI engine optimizes your resume, highlighting relevant skills and experience for the specific job.
              </p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <FaFileDownload size={24} />
              </div>
              <h3 className="feature-title">Download & Apply</h3>
              <p className="feature-text">
                Get your perfectly tailored resume in PDF format, ready to submit with your job application.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Benefits section */}
      <section className="benefits-section">
        <div className="container">
          <div className="benefits-content">
            <div className="section-header">
              <h2>Why Tailor Your Resume? 🚀</h2>
              <p className='benefits-content-para'>Stand out from the competition and get more interviews</p>
            </div>
            
            <ul className="benefits-list">
              <li className="benefit-item">
                <FaCheckCircle size={20} color="#10b981" />
                <span> Get past Applicant Tracking Systems (ATS) by matching key job requirements</span>
              </li>
              <li className="benefit-item">
                <FaCheckCircle size={20} color="#10b981" />
                <span> Highlight your most relevant experience for each position</span>
              </li>
              <li className="benefit-item">
                <FaCheckCircle size={20} color="#10b981" />
                <span> Show employers you're serious about the specific role</span>
              </li>
              <li className="benefit-item">
                <FaCheckCircle size={20} color="#10b981" />
                <span> Save time with our automated tailoring process</span>
              </li>
              <li className="benefit-item">
                <FaCheckCircle size={20} color="#10b981" />
                <span> Increase your interview callback rate by up to 60% ✅</span>
              </li>
            </ul>
            
            {!isLoggedIn && (
              <Link to="/register" className="btn btn-primary">
                <FaUserPlus size={18} /> Create Free Account
              </Link>
            )}
          </div>
          
          <div className="benefits-image">
            <img 
              src={ResumePointsTransparent} 
              alt="Resume comparison illustration" 
              className="benefits-img"
            />
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>What Our Users Say 💬</h2>
            <p className='testimonial-section-para'>Join thousands of job seekers who have found success with our platform</p>
          </div>
          
          <div className="testimonials-list">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p className="testimonial-text">
                  "I was applying to jobs for months with no responses. After tailoring my resume with this tool, I got an interview calls in just one week!"
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span>SP</span>
                </div>
                <div className="author-info">
                  <h4>Anupama P.</h4>
                  <p>Business Analyst</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p className="testimonial-text">
                  "The tailoring feature helped me emphasize different aspects of my experience for each application. It made all the difference in my job search."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span>VM</span>
                </div>
                <div className="author-info">
                  <h4>Venkatesh M.</h4>
                  <p>Software Engineer</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p className="testimonial-text">
                  "As a career changer, I struggled to show how my skills transferred. This tool helped me highlight relevant experience for each role I applied to."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">
                  <span>VP</span>
                </div>
                <div className="author-info">
                  <h4>Naveen G.</h4>
                  <p>Full Stack Engineer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Land Your Dream Job? 🎯</h2>
            <p>Create a free account and start tailoring your resume today.</p>
            {isLoggedIn ? (
              <Link to="/tailor" className="btn btn-primary btn-lg">
                <RiMagicLine size={20} /> Tailor My Resume Now
              </Link>
            ) : (
              <Link to="/register" className="btn btn-primary btn-lg">
                <FaRocket size={18} /> Get Started for Free
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;